import { removeSlashes, getEnvVariable } from "../utils/generalUtils";
import { showNetworkError, showStaleTokenAlert, isStaleTokenMessage } from "../utils/generalUtils";
import { navigate } from "../utils/navigation";
import { logException } from "./LoggingService";

import {
    refreshTokenIsExpired,
    accessTokenIsExpired,
    accessTokenIsFresh,
    accessTokenExpiration,
    getAccessToken,
    setAccessToken,
    getRefreshToken,
    setRefreshToken,
} from "../utils/tokenUtils";

// Rest of the file remains unchanged
export /* async */ function buildUrl(...urlSegments) {

    if (!urlSegments[0]) throw new Error('parameter is required (urlSegments)');

    const API_URL = getEnvVariable("VITE_APP_API_HOST_URL"); 
    const API_VERSION = getEnvVariable("VITE_APP_API_VERSION"); 
    // const ALTAPI_URL = getEnvVariable("VITE_APP_API_HOST_URL") || "ENV-VAR: VITE_API_URL NOT SET"; 
    // const ALTAPI_VERSION = getEnvVariable("VITE_APP_API_VERSION") || "ENV-VAR: VITE_API_VERSION NOT SET"; 

    let apiHost = removeSlashes(API_URL); 
    let url = `${apiHost}/api/${API_VERSION}`; 

    urlSegments.forEach(function(segment, index) {
        if (segment) {
            let cleanSegment = removeSlashes(segment);
            if (typeof cleanSegment === "string" && cleanSegment.startsWith("?")) {
                url += cleanSegment;  // append query string directly
            } else {
                url += "/" + cleanSegment;
            }
            // url += "/" + cleanSegment;
        }
    });

    // console.log( "------------------------------------");
    // console.log( "buildUrl-inputs: " + urlSegments);
    // console.log( "buildUrl-result: " + url);
    // console.log( "------------------------------------");

    return url;
}

// Explicit-version variant — use when a specific API version is needed (e.g., v2 endpoints)
// rather than the environment-variable default used by buildUrl.
export function buildVersionedUrl(version, ...urlSegments) {

    if (!version) throw new Error('parameter is required (version)');
    if (!urlSegments[0]) throw new Error('parameter is required (urlSegments)');

    const API_URL = getEnvVariable("VITE_APP_API_HOST_URL");

    let apiHost = removeSlashes(API_URL);
    let url = `${apiHost}/api/${version}`;

    urlSegments.forEach(function(segment) {
        if (segment) {
            let cleanSegment = removeSlashes(segment);
            if (typeof cleanSegment === "string" && cleanSegment.startsWith("?")) {
                url += cleanSegment;
            } else {
                url += "/" + cleanSegment;
            }
        }
    });

    return url;
}

// Add the missing buildAuthUrl function
export function buildAuthUrl(...urlSegments) {

    if (!urlSegments[0]) throw new Error('parameter is required (urlSegments)');

    const API_VERSION = getEnvVariable("VITE_APP_API_VERSION"); 
    const authApiBaseUrl = getEnvVariable("VITE_APP_AUTH_API_HOST_URL"); 
    const cleanAuthApiBaseUrl = removeSlashes(authApiBaseUrl); 
    // WRONG -- should not be including `/api` or `/v1` 
    // let url = `${cleanAuthApiBaseUrl}/api/${API_VERSION}`; 
    let url = `${cleanAuthApiBaseUrl}`; 

    urlSegments.forEach(function(segment, index) {
        if (segment) {
            let cleanSegment = removeSlashes(segment);
            if (typeof cleanSegment === "string" && cleanSegment.startsWith("?")) {
                url += cleanSegment;  // append query string directly
            } else {
                url += "/" + cleanSegment;
            }
            // url += "/" + cleanSegment;
        }
    });

    return url;
}

// variable to hold the promise for refreshing the access token -- purpose is to prevent multiple simultaneous refresh requests
let refreshingPromise = null;  
const refreshAccessToken = async () => {

    // build the URL for refreshing the access token (BADDOG)
    const url = buildAuthUrl('auth/refresh'); 
    
    // Ensure ONLY ONE refresh at a time!
    if (!refreshingPromise) {
        refreshingPromise =  fetchWithRefreshToken(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        })
        .then(async res => res.json())
        .then(async data => {
            
            setAccessToken(data.access_token);
            setRefreshToken(data.refresh_token);

            // accessTokenExpiry = decodeJwtExp(data.access_token);
            refreshingPromise = null;

            // return accessToken;
        })
        .catch(err => {
          refreshingPromise = null;
          throw err;
        });
    }

    return refreshingPromise;
  }

function requestRequiresFreshToken(options = {}) {

    // if parameter: `options` is not passed 
    // OR if `options` does not have the property `requireFreshToken`, 
    // then we assume that a fresh token is NOT required for this request
    if (!options || !options.hasOwnProperty('requireFreshToken')) return false;

    if (options.requireFreshToken === false) return false;
    if (options.requireFreshToken === true) return true;

    const method = String(options.method || 'GET').toUpperCase();
    return method !== 'GET' && method !== 'HEAD';
}

function assertFreshTokenForRequest(options = {}) {
    if (!requestRequiresFreshToken(options)) return;
    if (accessTokenIsFresh()) return;

    showStaleTokenAlert();
    const error = new Error('The token is not fresh.');
    error.code = 'TOKEN_NOT_FRESH';
    throw error;
}

export const fetchWithAccessToken = async (url, options = {}, handleResponseNotOk) => {

    if (accessTokenIsExpired()){ 
        console.log("fetchWithAccessToken: ACCESS_TOKEN is expired, refreshing it now.");
        await refreshAccessToken();
    }

    assertFreshTokenForRequest(options);

    // get the cached ACCESS-TOKEN from localStorage for calling the DataAPI
    const token = getAccessToken();

    if (!token || token === 'undefined' || token === null || token === '') {
        console.error("fetchWithAccessToken: No ACCESS_TOKEN was found in localStorage. This is an ERROR CONDITION -- all HTTP calls to DataAPI and/or BADDOG require this ACCESS_TOKEN.");
        showNetworkError("ERROR! No cached ACCESS_TOKEN was found.", "Please sign out and sign in and then try the operation again.");
        localStorage.setItem('httpServiceError', "");
        // TESTING 
        window.location = "/ErrorNoAccessToken";    
        // TESTING return;
        return;
    }

    return fetchWithToken(url, token, options, handleResponseNotOk) ;
}

export const fetchWithRefreshToken = async (url, options = {}, handleResponseNotOk) => {

    if (refreshTokenIsExpired()){ 
        console.warn("fetchWithRefreshToken: REFRESH_TOKEN is expired -- INTERACTIVE USER LOGIN required.");
        navigate("/signout");
        return;
    }

    // get the cached ACCESS-TOKEN from localStorage for calling the DataAPI
    // const token = localStorage.getItem('authToken');
    const token = getRefreshToken();

    if (!token || token === 'undefined' || token === null || token === '') {
        console.error("fetchWithRefreshToken: No REFRESH_TOKEN was found in localStorage. This is an ERROR CONDITION -- some HTTP calls to DataAPI and/or BADDOG require this ACCESS_TOKEN.");
        showNetworkError("ERROR! No cached REFRESH_TOKEN was found.", "Please sign out and sign in and then try the operation again.");
        localStorage.setItem('httpServiceError', "");
        // TESTING 
        window.location = "/ErrorNoAccessToken";    
        // TESTING return;
        return;
    }

    return fetchWithToken(url, token, options, handleResponseNotOk) ;
}

export const fetchWithToken = async (url, token, options = {}, handleResponseNotOk) => {
    
    const headers = {
      ...options.headers,
      'Authorization': token ? `Bearer ${token}` : '',
    };
    
    try {
        
        const response = await fetch(url, {
            ...options,
            headers,
        });

        if (!response.ok) {

            console.log(`fetchWithAccessToken: ${url} RESPONSE NOT OK: `, response.status, response.statusText);
            
            if (handleResponseNotOk) {
                return handleResponseNotOk(response);
            } else {
                return handleResponseNotOk_default(response);
            }
            
        }
    
        return response;

    } catch (error) {

        logException(error);

        // console.log(error);
        localStorage.setItem('httpServiceError', error);

        if (error.message.toLowerCase().includes("token has expired")) {
            showNetworkError("ERROR! API Server is reporting that your AUTHORIZATION TOKEN has expired", "Please sign out and sign in and then try the operation again.");
        }
        else if (error.message.toLowerCase().includes("Request does not contain an access token")) {
            showNetworkError("ERROR! API Server is reporting that your request is MISSING ACCESS TOKEN", "This is an error condition.");
        }
        else if (error.message.includes(" 401 ")) {
            // Check if this is a "user not in Telescope DB" error
            const storedError = localStorage.getItem('httpServiceError');
            if (storedError && storedError.toLowerCase().includes("authenticated user does not exist in telescope")) {
                window.location = "/ErrorAccountNotConfigured";
            } else {
                window.location = "/ErrorUnauthorized";
            }
        }
        else if (error.message.includes(" 403 ")) {
            window.location = "/ErrorForbidden";
        }
        else if (error.message.includes(" 405 ")) {
            window.location = "/ErrorMethodNotAllowed";    
        }
        else if (error.message.includes(" 409 ")) {
            window.location = "/ErrorConflict";    
        }
        else if (error.message.includes(" 417 ")) {
            window.location = "/ErrorExpectationFailed";    
        }
        else if (error.message.includes(" 422 ")) {
            window.location = "/ErrorUnprocessableEntity";    
        }
        else if (error.message.includes(" 500 ")) {
            window.location = "/ErrorServerError";    
        }
        else if (error.message.toLowerCase().includes("failed to fetch")) {
            // window.location = "/ErrorApiserver";
            showNetworkError("ERROR! API Server is NOT AVAILABLE", `Check that the Server is running and try again.\r\n\r\n${url}`);
        }

        return error;
    }
};

const handleResponseNotOk_default = async (response) => {

    // TODO: decide if this stays or goes
    const body = await response.json();

    let errorMessage = getResponseErrorMessages(body);
    errorMessage += "\n(HTTP error: " + response.status + ")";
    localStorage.setItem('httpServiceError', errorMessage);

    if (errorMessage.toLowerCase().includes("token has expired")) {   
        showNetworkError("ERROR! API Server is reporting that your AUTHORIZATION TOKEN has expired", "Please sign out and sign in and then try the operation again.");  
        // navigate("/signout");
        return response;
    }
    else if (isStaleTokenMessage(errorMessage)) {
        showStaleTokenAlert();
        return response;
    }
    else if (errorMessage.toLowerCase().includes("Request does not contain an access token")) {
        showNetworkError("ERROR! API Server is reporting that your request is MISSING ACCESS TOKEN", "This is an error condition.");
        return response;
    }
    else if (errorMessage.toLowerCase().includes("error: 401")) {
        // Check if this is a "user not in Telescope DB" error
        if (errorMessage.toLowerCase().includes("authenticated user does not exist in telescope")) {
            navigate("/ErrorAccountNotConfigured");
        } else {
            navigate("/ErrorUnauthorized");
        }
        return response;
    }
    else if (errorMessage.toLowerCase().includes("error: 403")) {
        navigate("/ErrorForbidden");
        return response;
    }
    else if (errorMessage.toLowerCase().includes("error: 405")) {
        navigate("/ErrorMethodNotAllowed");    
        return response;
    }
    else if (errorMessage.toLowerCase().includes("error: 409")) {
        navigate("/ErrorConflict");
        return response;
    }
    else if (errorMessage.toLowerCase().includes("error: 417")) {
        window.location = "/ErrorExpectationFailed";    
    }
    else if (errorMessage.toLowerCase().includes("error: 422")) {
        navigate("/ErrorUnprocessableEntity");
        return response;
    }
    else if (errorMessage.toLowerCase().includes("error: 500")) {
        navigate("/ErrorServerError");
        return response;
    }
    else if (errorMessage.toLowerCase().includes("failed to fetch")) {
        // window.location = "/ErrorApiserver";
        showNetworkError("ERROR! API Server is NOT AVAILABLE", "Please check that the Server is running and try again.");
        return response;
    }
}

export const getResponseErrorMessages =  (body) => {

    // let errorMessage = '\n';
    let errorMessage = '';

    if ('errors' in body) {
        if ('json' in body.errors) {
            Object.keys(body.errors.json).forEach(key => {
                errorMessage += key + ": " + body.errors.json[key] + "\n";
            });
        }
    }

    errorMessage += (typeof( body?.msg ) === 'string' ) ? body?.msg : '';
    errorMessage += (typeof( body?.message ) === 'string') ? body?.message : '';
    errorMessage += (typeof( body?.message?.message) === 'string') ? body?.message?.message : '';

    return errorMessage;
}

export const dataApiServerIsRunning = async () => {

    try {
        const url = buildUrl("ping");
        const response = await fetchWithToken(url);
        if (response && response.message && response.message.includes("Failed to fetch")) {
            return false;
        }
        const isOk = response.statusText === "OK" ? true : false;

        // TODO: decide if this stays or goes
        const body = await response.text();
        // TODO: decide if this stays or goes
    
        console.log("PING RESPONSE (DataAPI): " + isOk);
        return true;
    } catch (error) {
        console.log("PING RESPONSE (DataAPI): ");
        console.error("dataApiServerIsRunning: ", error);
        return false;
    }
}

export const dataApiServerVersion = async () => {

    try {
        const url = buildUrl("ping");
        const response = await fetchWithToken(url);
        if (response && response.message && response.message.includes("Failed to fetch")) {
            return "";
        }
        
        // TODO: decide if this stays or goes
        const result = await response.text();
        // TODO: decide if this stays or goes  
        console.log("PING RESPONSE (DataAPI): " + result);
        return result;
    } catch (error) {
        console.log("PING RESPONSE (DataAPI): ");
        console.error("dataApiServerVersion: ", error);
        return "";
    }
}


export const badDogApiServerIsRunning = async () => {

    try {
        const url = buildAuthUrl("ping");
        const response = await fetchWithToken(url);
        if (response && response.message && response.message.includes("Failed to fetch")) {
            return false;
        }
        const isOk = response.statusText === "OK" ? true : false;
        console.log("PING RESPONSE: " + isOk);
        return true;
    } catch (error) {
        console.log("PING RESPONSE: ");
        console.error("dataApiServerIsRunning: ", error);
        return false;
    }
}

export const badDogApiServerVersion = async () => {

    try {
        const url = buildAuthUrl("ping");
        const response = await fetchWithToken(url);
        if (response && response.message && response.message.includes("Failed to fetch")) {
            return "";
        }
        
        // TODO: decide if this stays or goes
        const result = await response.text();
        // TODO: decide if this stays or goes  
        console.log("PING RESPONSE (BadDogAPI): " + result);
        return result;
    } catch (error) {
        console.log("PING RESPONSE (BadDogAPI): ");
        console.error("badDogApiServerVersion: ", error);
        return "";
    }
}


export const dataApiDatawarehouseIsOk = async () => {

    try {
        const url = buildUrl("ping/dw");
        const response = await fetchWithToken(url);
        if (response && response.message && response.message.includes("Failed to fetch")) {
            return false;
        }
        const isOk = response.statusText === "OK" ? true : false;
        console.log("PING RESPONSE: " + isOk);
        return true;
    } catch (error) {
        console.log("PING RESPONSE: ");
        console.error("dataApiDatawarehouseIsOk: ", error);
        return false;
    }
}

export const dataApiTenantDbIsOk = async () => {

    try {
        const url = buildUrl("ping/tenantdb");

        // THIS ENDPOINT NOW REQUIRES an ACCESS TOKEN 
        const response = await fetchWithAccessToken(url);
        // const response = await fetchWithToken(url);

        if (response && response.message && response.message.includes("Failed to fetch")) {
            return false;
        }
        const isOk = response.statusText === "OK" ? true : false;
        console.log("PING RESPONSE: " + isOk);
        return true;
    } catch (error) {
        console.log("PING RESPONSE: ");
        console.error("dataApiTenantDbIsOk: ", error);
        return false;
    }
}

export const dataApiMaterializedViewsAreOk = async () => {

    try {
        const url = buildUrl("health/materialized-views");
        const response = await fetchWithAccessToken(url);
        if (response && response.message && response.message.includes("Failed to fetch")) {
            return { success: false, error: "Failed to fetch" };
        }
        
        // Read the response body into a JavaScript object
        const responseBody = await response.json();

        // Check if the response is OK
        const isOk = response.status === 200 && response.ok;
        console.log("Materialized Views Health Check Response:", responseBody);
        
        return {
            success: isOk,
            data: responseBody,
            status: response.status,
            statusText: response.statusText
        };
        
    } catch (error) {
        console.log("/health/materialized-views RESPONSE ERROR: ");
        console.error(error);
        return { 
            success: false, 
            error: error.message || "Unknown error occurred",
            data: null 
        };
    }
}

export const checkOllamaConnection = async () => {

    try {
        const url = buildUrl("ping/ollama");
        console.log("checkOllamaConnection: Calling URL:", url);
        
        const response = await fetchWithToken(url);
        console.log("checkOllamaConnection: Response received:", response);
        
        if (response && response.message && response.message.includes("Failed to fetch")) {
            console.log("checkOllamaConnection: Failed to fetch detected");
            return false;
        }
        const isOk = response.statusText === "OK" ? true : false;
        console.log("PING OLLAMA RESPONSE: " + isOk);
        return true;
    } catch (error) {
        console.log("PING OLLAMA RESPONSE ERROR: ");
        console.error("checkOllamaConnection: ", error);
        return false;
    }
}


const HttpService = {
    buildUrl,
    buildAuthUrl,
    fetchWithAccessToken: fetchWithAccessToken,
    fetchWithToken,
    showNetworkError,
    dataApiServerIsRunning,
    dataApiDatawarehouseIsOk,
    dataApiTenantDbIsOk,
    checkOllamaConnection,
    badDogApiServerIsRunning
};
 
 export default HttpService;